export type AttributeOption = {
  value: string
  label: string
  // An option is a tenant's own vocabulary row, and config may hang further
  // keys off it that this editor knows nothing about. They survive the
  // round-trip untouched - rebuilding the row as {value,label} silently
  // deleted them the first time anyone opened the dialog.
  [key: string]: unknown
}

export type AttributeDefaultValue = string | number | boolean | string[] | null

export function toSnakeLowercaseKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function isChoiceAttributeFieldType(fieldType: string) {
  return (
    fieldType === 'SELECT' ||
    fieldType === 'MULTI_SELECT' ||
    fieldType === 'DOUBLE_SELECT'
  )
}

export function isMultiChoiceAttributeFieldType(fieldType: string) {
  return fieldType === 'MULTI_SELECT'
}

export function supportsAttributeDefaultValue(fieldType: string) {
  return !['DOCUMENT', 'FILE', 'IMAGE', 'VIDEO'].includes(fieldType)
}

export function optionValuesFromConfig(options: unknown): AttributeOption[] {
  if (!Array.isArray(options)) return []
  return options
    .map((option) => {
      if (!isRecord(option)) return null
      const label = String(option.label ?? option.value ?? '').trim()
      const value = toSnakeLowercaseKey(String(option.value ?? label))
      if (!value || !label) return null
      return { ...option, value, label }
    })
    .filter((option): option is AttributeOption => option !== null)
}

export function optionsToPayload(options: AttributeOption[]) {
  return options
    .map((option) => ({
      ...option,
      value: toSnakeLowercaseKey(option.value || option.label),
      label: option.label.trim(),
    }))
    .filter((option, index, all) => {
      if (!option.value || !option.label) return false
      return all.findIndex((candidate) => candidate.value === option.value) === index
    })
}

export function defaultValueFromConfig(
  fieldType: string,
  value: unknown,
  options: AttributeOption[] = [],
): AttributeDefaultValue {
  if (value === undefined) return null
  return normaliseDefaultValue(fieldType, value, options)
}

export function normaliseDefaultValue(
  fieldType: string,
  value: unknown,
  options: AttributeOption[] = [],
): AttributeDefaultValue {
  if (!supportsAttributeDefaultValue(fieldType)) return null

  if (fieldType === 'BOOLEAN') {
    return Boolean(value)
  }

  if (fieldType === 'MULTI_SELECT') {
    if (!Array.isArray(value)) return []
    const allowedValues = new Set(options.map((option) => option.value))
    return value
      .map((item) => toSnakeLowercaseKey(String(item)))
      .filter((item) => item.length > 0)
      .filter((item) => allowedValues.size === 0 || allowedValues.has(item))
  }

  if (fieldType === 'SELECT' || fieldType === 'DOUBLE_SELECT') {
    const key = toSnakeLowercaseKey(String(value ?? ''))
    const allowedValues = new Set(options.map((option) => option.value))
    if (allowedValues.size > 0 && !allowedValues.has(key)) return null
    return key || null
  }

  if (fieldType === 'NUMBER' || fieldType === 'MONEY') {
    if (value === '' || value === null || value === undefined) return null
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  }

  const text = String(value ?? '').trim()
  return text || null
}

export function hasDefaultValue(value: AttributeDefaultValue) {
  if (Array.isArray(value)) return value.length > 0
  return value !== null && value !== ''
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
